<template>
    <div>
        <h2>Date-Based Tasks & Warnings</h2>
        <v-card fill-height class="scheduled">
            <v-data-table :items="this.dateBasedTasks" :items-per-page="options.itemsPerPage" :headers="headers" :loading="$wait.is(['loading unit date based tasks'])" :footer-props.sync="footerProps" :options.sync="options">
                <v-progress-linear v-slot:progress color="blue" indeterminate></v-progress-linear>
                <template v-slot:item="items">
                    <tr :class="classes(items.item)">
                        <td>
                            {{ items.item.dueDate | formatDate }}
                        </td>
                        <td>
                            <v-chip
                                v-if="items.item.dueAtTolerance != 0"
                                class="mr-2"
                                :key="items.item.id + '-tolerance'"
                                label
                                small
                                :color="items.item.dueAtTolerance > 0 ? 'red' : null"
                                :dark="items.item.dueAtTolerance > 0 ? true : null"
                            >
                                {{ items.item.dueAtTolerance > 0 ? `+${items.item.dueAtTolerance}` : items.item.dueAtTolerance }}&nbsp;days
                            </v-chip>
                        </td>
                        <td>{{ items.item.description }}</td>
                        <td v-if="items.item.interval">Every {{ items.item.interval }} {{ items.item.intervalType}}</td>
                        <td v-else>Never</td>
                        <td v-if="!unit.isDeleted && (userIsFleetManager || userIsDataEntryInGarage(unit.garageLocationId, userGarages))">
                            <v-btn icon @click="openEdit(items.item)">
                                <v-icon>edit</v-icon>
                            </v-btn>
                            <v-btn v-if="!unit.isDeleted && userIsFleetManager" @click="confirmDelete(items.item)" icon small color="red">
                                <v-icon>delete</v-icon>
                            </v-btn>
                        </td>
                    </tr>
                </template>
            </v-data-table>
            <v-card-actions v-if="!disabled">
                <v-spacer></v-spacer>
                <v-btn color="primary" @click="openAdd()">Add Date-Based Task</v-btn>
                <v-dialog v-model="dialog" max-width="500px" persistent>
                    <Form v-model="model" @add="addTask($event.id, $event.model)" @edit="updateTask($event.id, $event.model)" @close="dialog = false">
                        <template v-slot:header>
                            <v-spacer></v-spacer>
                            <v-btn @click="dialog = false" icon text>
                                <v-icon>close</v-icon>
                            </v-btn>
                        </template>
                    </Form>
                </v-dialog>
            </v-card-actions>
        </v-card>
    </div>
</template>
<script>
import DateBasedTask from '@/components/DateBasedTasks/DateBasedTasksClient';
import GarageLocationsClient from '@/components/GarageLocations/GarageLocationsClient.vue';
import Form from '@/components/DateBasedTasks/Form.vue';
import { Event } from '@/plugins/bus';

export default {
    props: { unit: { required: true } },
    mixins: [GarageLocationsClient, DateBasedTask],
    components: {
        Form
    },
    data() {
        return {
            dateBasedTasks: [],
            dialog: false,
            model: {},
            options: {
                itemsPerPage: 10,
                sortBy: ['name']
            },
            footerProps: {
                itemsPerPageOptions: [10, 50, 100]
            },
            headers: [
                { text: 'Due Date', value: 'date', sortable: true },
                { text: '', value: 'tolerance', sortable: false },
                { text: 'Task', value: 'task', sortable: true },
                { text: 'Repeats', value: 'repeats', sortable: false },
                { text: '', value: 'actions', sortable: false }
            ]
        };
    },
    computed: {
        unitId() {
            return this.$route.params.unitId;
        },
        params() {
            return {
                pageSize: this.options && this.options.itemsPerPage,
                page: this.options && this.options.page,
                completed: true,
                orderBy: '-date',
                unitId: this.unitId,
                excludeReadOnly: true,
                includeDeleted: true
            };
        },
        disabled() {
            const g = this.unit.garageLocationId;
            return this.unit.isDeleted || (!this.userIsOperationsInGarage(g, this.userGarages) && !this.userIsDataEntryInGarage(g, this.userGarages) && !this.userIsWorkshopManagerInGarage(g, this.userGarages) && !this.userIsFleetManager);
        }
    },
    methods: {
        openAdd() {
            this.model = {
                unitId: this.unitId,
                recurring: false
            };
            this.dialog = true;
        },
        openEdit(item) {
            this.model = {
                ...item,
                unitId: this.unitId,
                recurring: !!item.interval,
                initialDate: item.dueDate
            };
            this.dialog = true;
        },
        async addTask(id, model) {
            await this.addDateBasedTask(id, model);
        },
        async updateTask(id, model) {
            await this.updateDateBasedTask(id, model);
        },
        async refreshDateBasedTasks() {
            this.$wait.start('loading unit date based tasks');
            const tasks = await this.getDateBasedTasksForUnit();
            this.dateBasedTasks = tasks.filter(t => !t.isDeleted);
            this.$wait.end('loading unit date based tasks');
        },
        async handleDateBasedTaskUpdated() {
            this.dialog = false;
            await this.refreshDateBasedTasks();
        },
        async handleDateBasedTaskDeleted() {
            await this.refreshDateBasedTasks();
        },
        confirmDelete(dateBasedTask) {
            this.$confirmCancel.show(`Delete Date-Based Task?`, `<p>This will permanently delete this date-based task record</p>`, 'Delete it');
            this.$confirmCancel.$on('confirm-cancel', confirmed => {
                if (!confirmed) {
                    return;
                }
                this.$bus.$emit(Event.DateBasedTaskPendingDelete, {
                    id: dateBasedTask.id
                });
            });
        },
        classes(dbt) {
            return {
                sweptForward: !!dbt.sweptForwardByScheduler
            };
        }
    },
    async mounted() {
        this.refreshDateBasedTasks();

        this.$bus.$on(Event.DateBasedTaskUpdated, this.handleDateBasedTaskUpdated);
        this.$bus.$on(Event.DateBasedTaskDeleted, this.handleDateBasedTaskDeleted);
    },
    destroyed() {
        this.$bus.$off(Event.DateBasedTaskUpdated, this.handleDateBasedTaskUpdated);
        this.$bus.$off(Event.DateBasedTaskDeleted, this.handleDateBasedTaskDeleted);
    },
    watch: {
        async unitId() {
            this.refreshDateBasedTasks();
        },
        options: {
            async handler() {
                await this.refreshDateBasedTasks();
            },
            deep: true
        }
    }
};
</script>
